import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Fade,
  Typography,
  Popover,
  Paper,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  AttachFile,
  EmojiEmotions,
  Send,
  Mic,
  Close,
  Stop,
  InsertDriveFile,
  Widgets,
  CameraAlt,
} from "@mui/icons-material";
import EmojiPicker from "emoji-picker-react";
import TemplatePicker from "./TemplatePicker";

const ChatInput = ({
  text,
  setText,
  onSend,
  replyTo,
  setReplyTo,
  contact,
  handleKeyPress,
  conversationId,
}) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const primaryDark = theme.palette.primary.dark;
  const [anchorEl, setAnchorEl] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [filePreview, setFilePreview] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
      setRecordingTime(0);
    }
    return () => clearInterval(timerRef.current);
  }, [isRecording]);

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleEmojiClick = (event) => setAnchorEl(event.currentTarget);
  const handleEmojiSelect = (emojiData) =>
    setText((prev) => prev + emojiData.emoji);
  const handleCloseEmoji = () => setAnchorEl(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setFilePreview({
          url: reader.result,
          name: file.name,
          type: file.type,
          raw: file,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setFilePreview({
          url: reader.result,
          name: file.name,
          type: file.type,
          raw: file,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mediaRecorderRef.current.ondataavailable = (e) =>
        audioChunksRef.current.push(e.data);
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        setAudioPreview(URL.createObjectURL(audioBlob));
      };
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setAudioPreview(null);
    } catch (err) {
      console.error(err);
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setIsRecording(false);
    mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop());
  };

  const clearPreviews = () => {
    setFilePreview(null);
    setAudioPreview(null);
  };

  const handleSendAction = () => {
    onSend({
      text,
      file: filePreview,
      audio: audioPreview,
    });
    setText("");
    clearPreviews();
  };

  const isTextEmpty = !text.trim();

  return (
    <Box
      sx={{
        bgcolor: "#ffffff",
        borderTop: "1px solid rgba(0, 0, 0, 0.06)",
      }}
    >
      {/* File/Audio Preview */}
      {(filePreview || audioPreview) && (
        <Fade in timeout={200}>
          <Box
            sx={{
              p: 2, // padding: 16px
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 1.5, // padding: 12px
                position: "relative",
                minWidth: 200,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                bgcolor: "#f8fafc",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              <IconButton
                size="small"
                onClick={clearPreviews}
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  bgcolor: "#fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  width: 24,
                  height: 24,
                  "&:hover": { bgcolor: "#f1f5f9" },
                }}
              >
                <Close sx={{ fontSize: 14 }} />
              </IconButton>
              {filePreview && (
                <>
                  {filePreview.type.startsWith("image/") ? (
                    <img
                      src={filePreview.url}
                      alt="preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: 150,
                        borderRadius: 8,
                      }}
                    />
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <InsertDriveFile sx={{ color: primaryColor }} />
                      <Typography variant="caption" sx={{ color: "#475569" }}>
                        {filePreview.name}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
              {audioPreview && (
                <audio
                  src={audioPreview}
                  controls
                  style={{ height: 35, maxWidth: "100%" }}
                />
              )}
            </Paper>
          </Box>
        </Fade>
      )}

      {/* Reply Preview */}
      {replyTo && (
        <Fade in={Boolean(replyTo)}>
          <Box
            sx={{
              px: 2.5, // padding-x: 20px
              py: 1.5, // padding-y: 12px
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderTop: "1px solid #e2e8f0",
              bgcolor: "#f8fafc",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  height: 36, // 36px
                  width: 4, // 4px
                  bgcolor: primaryColor,
                  borderRadius: 2,
                }}
              />
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: primaryColor,
                    fontWeight: 600,
                    fontSize: "0.75rem", // 12px
                  }}
                >
                  Replying to {contact.client_name || contact.phone}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "#64748b",
                    fontSize: "0.8125rem", // 13px
                    mt: 0.25,
                  }}
                  noWrap
                >
                  {replyTo.text}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={() => setReplyTo(null)}
              sx={{
                color: "#94a3b8",
                "&:hover": { color: "#64748b", bgcolor: "#e2e8f0" },
              }}
            >
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Fade>
      )}

      {/* Main Input Area */}
      <Box
        sx={{
          px: 2, // padding-x: 16px
          py: 1.5, // padding-y: 12px
          display: "flex",
          alignItems: "flex-end",
          gap: 1, // gap: 8px
        }}
      >
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <input
          type="file"
          ref={cameraInputRef}
          accept="image/*"
          capture="environment"
          style={{ display: "none" }}
          onChange={handleCameraCapture}
        />

        {/* Left Action Icons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* Camera Icon */}
          <IconButton
            onClick={() => cameraInputRef.current.click()}
            title="Take Photo"
            sx={{
              color: "#64748b",
              width: 40, // 40px
              height: 40, // 40px
              borderRadius: "10px",
              transition: "all 0.2s ease",
              "&:hover": {
                color: primaryColor,
                bgcolor: alpha(primaryColor, 0.08),
              },
            }}
          >
            <CameraAlt sx={{ fontSize: 22 }} />
          </IconButton>

          {/* Attachment Icon */}
          <IconButton
            onClick={() => fileInputRef.current.click()}
            title="Attach File"
            sx={{
              color: "#64748b",
              width: 40, // 40px
              height: 40, // 40px
              borderRadius: "10px",
              transition: "all 0.2s ease",
              "&:hover": {
                color: primaryColor,
                bgcolor: alpha(primaryColor, 0.08),
              },
            }}
          >
            <AttachFile sx={{ fontSize: 22 }} />
          </IconButton>

          {/* Template Picker Icon */}
          <IconButton
            onClick={() => setTemplatePickerOpen(true)}
            title="Send Template"
            sx={{
              color: "#64748b",
              width: 40, // 40px
              height: 40, // 40px
              borderRadius: "10px",
              transition: "all 0.2s ease",
              "&:hover": {
                color: primaryColor,
                bgcolor: alpha(primaryColor, 0.08),
              },
            }}
          >
            <Widgets sx={{ fontSize: 22 }} />
          </IconButton>

          {/* Emoji Picker Icon */}
          <IconButton
            onClick={handleEmojiClick}
            sx={{
              color: anchorEl ? primaryColor : "#64748b",
              width: 40, // 40px
              height: 40, // 40px
              borderRadius: "10px",
              transition: "all 0.2s ease",
              "&:hover": {
                color: primaryColor,
                bgcolor: alpha(primaryColor, 0.08),
              },
            }}
          >
            <EmojiEmotions sx={{ fontSize: 22 }} />
          </IconButton>
        </Box>

        {/* Emoji Picker Popover */}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCloseEmoji}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          PaperProps={{
            sx: {
              borderRadius: "16px",
              boxShadow: "0 20px 40px -8px rgba(0, 0, 0, 0.15)",
              border: "1px solid rgba(0, 0, 0, 0.06)",
              overflow: "hidden",
              "& .epr-body::-webkit-scrollbar": {
                width: "6px",
              },
              "& .epr-body::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "& .epr-body::-webkit-scrollbar-thumb": {
                background: primaryColor,
                borderRadius: "10px",
              },
              "& .epr-body::-webkit-scrollbar-thumb:hover": {
                background: primaryDark,
              },
              "& .epr-body": {
                scrollbarWidth: "thin",
                scrollbarColor: `${primaryColor} transparent`,
              },
            },
          }}
        >
          <EmojiPicker
            onEmojiClick={handleEmojiSelect}
            autoFocusSearch={false}
            searchDisabled={false}
            skinTonesDisabled
            previewConfig={{ showPreview: false }}
            height={400}
            width={320}
          />
        </Popover>

        {/* Text Input Field */}
        <Box sx={{ flex: 1, position: "relative" }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            value={isRecording ? "" : text}
            disabled={isRecording}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={isRecording ? "" : "Type a message..."}
            InputProps={{
              sx: {
                bgcolor: "#f8fafc",
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                px: 1.5, // padding-x: 12px
                py: 0.5, // padding-y: 4px
                minHeight: 48, // min-height: 48px
                transition: "all 0.2s ease",
                "& fieldset": { border: "none" },
                "&:hover": {
                  borderColor: "#cbd5e1",
                },
                "&.Mui-focused": {
                  borderColor: primaryColor,
                  boxShadow: `0 0 0 3px ${alpha(primaryColor, 0.1)}`,
                },
              },
              startAdornment: isRecording && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "#ef4444",
                    mr: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 10, // 10px
                      height: 10, // 10px
                      bgcolor: "#ef4444",
                      borderRadius: "50%",
                      "@keyframes pulse": {
                        "0%": { opacity: 1, transform: "scale(1)" },
                        "50%": { opacity: 0.5, transform: "scale(1.2)" },
                        "100%": { opacity: 1, transform: "scale(1)" },
                      },
                      animation: "pulse 1s infinite",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                  >
                    {formatTimer(recordingTime)}
                  </Typography>
                </Box>
              ),
            }}
          />
        </Box>

        {/* Right Action Buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* Send Button */}
          <IconButton
            onClick={
              !isTextEmpty || filePreview || audioPreview
                ? handleSendAction
                : isRecording
                ? stopRecording
                : startRecording
            }
            sx={{
              width: 48, // 48px
              height: 48, // 48px
              borderRadius: "14px",
              bgcolor:
                !isTextEmpty || filePreview || audioPreview
                  ? primaryColor
                  : isRecording
                  ? "#ef4444"
                  : "transparent",
              color:
                !isTextEmpty || filePreview || audioPreview || isRecording
                  ? "#fff"
                  : "#64748b",
              boxShadow:
                !isTextEmpty || filePreview || audioPreview
                  ? `0 4px 14px ${alpha(primaryColor, 0.35)}`
                  : isRecording
                  ? "0 4px 14px rgba(239, 68, 68, 0.35)"
                  : "none",
              transition: "all 0.3s ease",
              "&:hover": {
                bgcolor:
                  !isTextEmpty || filePreview || audioPreview
                    ? primaryDark
                    : isRecording
                    ? "#dc2626"
                    : "#f1f5f9",
                transform:
                  !isTextEmpty || filePreview || audioPreview || isRecording
                    ? "scale(1.05)"
                    : "none",
              },
            }}
          >
            {!isTextEmpty || filePreview || audioPreview ? (
              <Send sx={{ fontSize: 22 }} />
            ) : isRecording ? (
              <Stop sx={{ fontSize: 22 }} />
            ) : (
              <Mic sx={{ fontSize: 22 }} />
            )}
          </IconButton>
        </Box>
      </Box>

      {/* Template Picker Modal */}
      <TemplatePicker
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        conversationId={conversationId}
        onTemplateSent={() => {
          setTemplatePickerOpen(false);
        }}
      />
    </Box>
  );
};

export default ChatInput;
